import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit } from '@angular/core';
import { ApiService, AssistResourceBM, confirmEvent } from '@cue/api';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import {
  AppState,
  AuthService,
  cancelParticipationEventById,
  ConfigService,
  DataService,
  Day,
  daysBetweemDates,
  deleteEventById,
  eventConfirmed,
  eventCreated,
  eventPatched,
  FavouritesHelper,
  finishEventById,
  getTextKeyAndClass,
  IdentifiedAssistEvent,
  injectResources,
  injectUser,
  isAllDayEvent,
  isDifferent,
  isIdentifiedAssistEvent,
  prepareEventForDetail,
  ResourceAttendee,
  setFloorplanFilterAreaId,
  setFloorplanFilterDateAndDuration,
  TimelineAttendeeItem,
  TimelineEvent,
  TimelineIdentifiedAssistEvent,
  timelineSizes,
  TimelineSizes,
} from '@assist/shared/data';
import {
  add,
  addHours,
  addMinutes,
  differenceInMinutes,
  getHours,
  getMinutes,
  isSameDay,
  set,
  startOfHour,
} from 'date-fns';
import { ReservationCardComponent } from '@assist/ui/scheduler';
import { ReservationDialogOpener } from '@assist/reservation-detail';
import {
  CancelParticipationData,
  FinishReservationData,
  ReservationCancelParticipationComponent,
  ReservationDeleteComponent,
  ReservationFinishComponent,
} from '@assist/ui/reservation-popups';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { Store } from '@ngrx/store';
import { LocalizedStringPipe, QRService } from '@cue/utilities';
import { ResponsiveService } from '@cue/responsivity';
import { NavigationRouteParams } from '@cue/navigation-services';
import { Router } from '@angular/router';
import { LoadingComponent } from '@cue/loading';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { translation } from '@cue/translations';
import { CalendarAttendee, CalendarAvailability } from '@cue/calendars';
import { map, takeUntil, timer } from 'rxjs';
import { AvailabilityCardComponent } from './availability-card.component';
import { NotificationService } from '@assist/notifications/services';
import { MultiviewComponent } from '../multiview/multiview.component';
import { Actions, ofType } from '@ngrx/effects';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { tap } from 'rxjs/operators';
import { ButtonComponent } from '@cue/ui';
import { cueStart, cueAdd, IconComponent, cueMarker } from '@cue/ui/icons';
import { NgClass, NgStyle } from '@angular/common';
import { DateFormatPipe, DateTimeFormatPipe, SubjectPipe } from '@cue/pipes';
import { ResourceTitlePipe } from '@assist/ui/pipes';
import { TooltipDirective, TooltipPositions } from '@cue/ui/tooltips';
import { floorToQuarterHour } from '@cue/data';
import { CalendarRecurringMutationScope } from '@cue/calendars';

@Component({
  selector: 'assist-multiview-events',
  imports: [
    ReservationCardComponent,
    LoadingComponent,
    TranslocoPipe,
    AvailabilityCardComponent,
    ButtonComponent,
    IconComponent,
    NgStyle,
    DateFormatPipe,
    DateTimeFormatPipe,
    LocalizedStringPipe,
    ResourceTitlePipe,
    SubjectPipe,
    NgClass,
    TooltipDirective,
  ],
  templateUrl: './multiview-events.component.html',
  styleUrl: './multiview-events.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiviewEventsComponent implements OnInit {
  isAnonymous = input(false);
  qrService = inject(QRService);
  apiService = inject(ApiService);
  authService = inject(AuthService);
  notificationService = inject(NotificationService);
  resource = input.required<AssistResourceBM>();
  dataService = inject(DataService);
  responsiveService = inject(ResponsiveService);
  dialog = inject(Dialog);
  reservationDialog = inject(ReservationDialogOpener);
  store = inject(Store<AppState>);
  user = injectUser();
  router = inject(Router);
  actions = inject(Actions);
  terminator = injectDestroy();
  eventChanged = this.actions.pipe(ofType(eventConfirmed, eventCreated, eventPatched));

  ICONS = {
    cueStart,
    cueAdd,
    cueMarker,
  };
  ngOnInit() {
    this.eventChanged
      .pipe(
        takeUntil(this.terminator),
        tap(() => {
          this.events.reload();
          this.schedules.reload();
        })
      )
      .subscribe();
  }
  _dialog?: DialogRef<any, any>;

  fastConfirmUntil = addMinutes(new Date(), 5);

  now = new Date();
  before2Hours = set(addHours(startOfHour(this.now), -2), {
    seconds: 0,
  });
  after24Hours = set(add(this.before2Hours, { hours: 26 }), {
    seconds: 0,
  });

  schedules = rxResource({
    params: () => this.resource(),
    stream: ({ params }) => {
      return this.dataService
        .getSchedulesForResources(
          [
            {
              requirement: {
                id: 'multiview',
              } as any,
              resources: [params],
              additional: false,
            },
          ],
          this.before2Hours,
          this.after24Hours
        )
        .pipe(
          map((response) => {
            return {
              schedule: response[0] ? response[0] : [],
              start: this.before2Hours,
            };
          })
        );
    },
  });

  private helper = new FavouritesHelper();

  days = computed(() => {
    const days: Day[] = daysBetweemDates(this.before2Hours, this.after24Hours).map(
      (d) =>
        ({
          date: d,
          hours: [],
          sunday: false,
          today: false,
        }) as Day
    );

    days.forEach((day, index) => {
      const hours = [];
      const startHour = isSameDay(day.date, this.before2Hours)
        ? getHours(this.before2Hours) + (getMinutes(this.before2Hours) > 0 ? 1 : 0)
        : 0;
      const endHour = isSameDay(day.date, this.after24Hours) ? getHours(this.after24Hours) - 1 : 23;

      for (let hour = startHour; hour <= endHour; hour++) {
        hours.push(hour);
      }

      const final = hours;

      day.hours = final;
    });

    return days;
  });

  now$ = timer(0, 60000).pipe(map(() => new Date()));

  nowLine = toSignal(this.now$);

  nowLeft = computed(() => {
    const now = this.nowLine();
    const start = this.before2Hours;
    const diff = differenceInMinutes(now, start);
    const left = (diff / 15) * 20;
    return left;
  });

  totalWidth = computed(() => {
    const sizes = this.timelineSizes();
    if (!sizes) {
      return 0;
    }
    // days  * hours * 4 quarters
    const totalQuarters = this.days().reduce((acc, day) => {
      return acc + day.hours.length * 4;
    }, 0);
    return totalQuarters * sizes.quarterWidth;
  });

  getPosition(event: TimelineIdentifiedAssistEvent | TimelineAttendeeItem, scrollable: HTMLDivElement) {
    const left = event.left;
    const scrollLeft = scrollable.scrollLeft;
    const xOffset = scrollLeft - left < 0 ? 0 : scrollLeft - left;
    return [
      {
        ...TooltipPositions.OVER_START,
        offsetX: xOffset,
      },
      {
        ...TooltipPositions.OVER_END,
      },
    ];
  }

  inNotResourceAttendee(attendee: CalendarAttendee, resourceAttendees: ResourceAttendee[]) {
    return (
      !attendee.resource &&
      !resourceAttendees.some((rA) => rA.resource.email.toUpperCase() !== attendee.emailAddress?.address?.toUpperCase())
    );
  }

  extractEvents(availabilityView: CalendarAvailability[], start: Date, end: Date, sizes: TimelineSizes) {
    let lastIndex = 0;
    let lastValue = availabilityView[lastIndex];
    const events: TimelineEvent[] = [];

    const getAvailability = (availability: CalendarAvailability) => {
      return availability === CalendarAvailability.free || availability === CalendarAvailability.freeOutsideWorkingHours
        ? CalendarAvailability.free
        : availability;
    };

    const addition = Math.floor(getMinutes(start) / 15) * sizes.quarterWidth;
    for (let i = 1; i <= availabilityView.length; i++) {
      if (isDifferent(availabilityView[i], lastValue)) {
        const finalAvailability = getAvailability(availabilityView[i - 1]);
        const event = {
          availabilityView: availabilityView.slice(lastIndex, i).join(''),
          availability: finalAvailability,
          height: sizes.scheduleHeight,
          width: (i - lastIndex) * sizes.quarterWidth,
          left: lastIndex * sizes.quarterWidth + addition,
          duration: (i - lastIndex + 1) * 15,
          top: 0, //(sizes.scheduleHeight + sizes.scheduleMargin * 2) * scheduleIndex,
          start: add(start, { minutes: lastIndex * 15 }),
          end: add(add(start, { minutes: lastIndex * 15 }), {
            minutes: (i - lastIndex) * 15,
          }),
          id: 'main',
        };

        events.push(event);
        lastIndex = i;
        lastValue = availabilityView[lastIndex];
      }
    }
    return events;
  }

  notResourceAttendeeLength(calendarAttendees: CalendarAttendee[], resourceAttendees: ResourceAttendee[]) {
    return calendarAttendees.filter((attendee) => this.inNotResourceAttendee(attendee, resourceAttendees)).length;
  }

  eventIsAllDay(event: { start: Date; end: Date }) {
    return isAllDayEvent(event.start, event.end);
  }

  getTextKeyForConfirmation(resourceAttendee: ResourceAttendee) {
    if (resourceAttendee.resource.confirmationSettings.requiresConfirmation) {
      return translation.reservations.confirmationRequiredBetween;
    }
    if (
      resourceAttendee.resource.confirmationSettings.requiresConfirmationOnPanel &&
      resourceAttendee.resource.confirmationSettings.requiresConfirmationBySensor
    ) {
      return translation.reservations.confirmationSensorOrPanelRequiredBetween;
    }
    return resourceAttendee.resource.confirmationSettings.requiresConfirmationOnPanel
      ? translation.reservations.confirmationPanelRequiredBetween
      : translation.reservations.confirmationSensorRequiredBetween;
  }

  getBackgroundColor(availability: CalendarAvailability): string {
    return this.configService.backgroundColor(availability);
  }

  getBackground(calendarAvailability: CalendarAvailability) {
    const backgroudColor = this.getBackgroundColor(calendarAvailability);
    const style = `
    repeating-linear-gradient(
      90deg,
      #FBFBFB,
      #c7bebe00 1px,
      ${backgroudColor} 1px,
      ${backgroudColor} ${this.timelineSizes()!.quarterWidth!}px
    )
    `;
    return style;
  }

  schedule = computed(() => {
    if (this.schedules.isLoading() || !this.schedules.hasValue()) {
      return null;
    }

    return this.schedules.value()!.schedule[0];
  });

  setHigherAlphaToRgba(color?: string, wantedAlpha = 0.9) {
    if (!color || color.startsWith('rgb(')) {
      return color;
    }

    const [r, g, b, a] = color.match(/\d+(\.\d+)?/g).map(Number);
    const adjustedAlpha = a < 0.9 ? wantedAlpha : a;

    return `rgba(${r}, ${g}, ${b}, ${adjustedAlpha})`;
  }

  attendeeItemStyle(item: TimelineAttendeeItem | IdentifiedAssistEvent) {
    let color = '';
    if (isIdentifiedAssistEvent(item)) {
      // todo: barbora - domyslen si barvy
      color = this.configService.backgroundColor(CalendarAvailability.reserved);
      //   ? this.configService.backgroundColor(CalendarAvailability.closed)
      //   : item.confirmed
      //     ? this.configService.backgroundColor(CalendarAvailability.reserved)
      //     : item.accepted
      //       ? this.configService.backgroundColor(CalendarAvailability.reserved)
      //       : this.configService.backgroundColor(CalendarAvailability);
    } else {
      color = this.configService.backgroundColor(item.availability);
    }

    let cursor = 'default';
    let saturatedColor = '';
    if (this.configService.isTouchoneCalendar() && isIdentifiedAssistEvent(item)) {
      if (item.eventPermissions.canEditPublic || item.eventPermissions.canEditPrivate) {
        cursor = 'pointer';
      }
    } else {
      if (!isIdentifiedAssistEvent(item)) {
        cursor = item.cancelled != true ? 'pointer' : 'default';
        color = this.configService.backgroundColor(CalendarAvailability.reserved);
        saturatedColor = this.configService.backgroundColor(CalendarAvailability.reserved);
      }
    }

    if (this.configService.isTouchoneCalendar() || isIdentifiedAssistEvent(item)) {
      saturatedColor = this.setHigherAlphaToRgba(color);
    }
    return {
      cursor,
      backgroundColor: color,
      '--event-hover-saturated': saturatedColor,
    };
  }

  scheduleEvents = computed(() => {
    if (this.schedules.isLoading() || !this.schedules.hasValue()) {
      return [];
    }

    return this.schedule().scheduleItems.map((si) => {
      const siStart = si.start.utcDateTime;
      const siEnd = si.end.utcDateTime;
      const scopedStart = this.before2Hours > siStart ? this.before2Hours : siStart;
      const scopedEnd = this.after24Hours < siEnd ? this.after24Hours : siEnd;
      const diffEndStart = Math.abs(differenceInMinutes(scopedStart, scopedEnd));
      const width = (diffEndStart / 15) * 20;
      const left = Math.abs((differenceInMinutes(this.before2Hours, scopedStart) / 15) * 20);
      return {
        ...si,
        width: width,
        left: left,
      };
    });
  });

  extractedEvents = computed(() => {
    if (this.schedules.isLoading() || !this.schedules.hasValue()) {
      return [] as TimelineEvent[];
    }
    const events = this.extractEvents(
      // remove first 2 hours
      this.schedules.value()!.schedule[0].availabilityView.split('') as CalendarAvailability[],
      this.before2Hours,
      this.after24Hours,
      this.timelineSizes()
    );

    return events;
  });
  timelineSizes = toSignal(this.store.select(timelineSizes));
  reservationPosibilities = computed(() => {
    if (this.schedules.isLoading() || !this.schedules.hasValue()) {
      return null;
    }

    const prevQuarter = floorToQuarterHour(this.now);
    const differenceInMinutesFromPrevQuarterToNow = differenceInMinutes(this.now, this.before2Hours);
    const slices = Math.floor(differenceInMinutesFromPrevQuarterToNow / 15);

    const events = this.extractEvents(
      // remove first 2 hours
      this.schedules.value()!.schedule[0].availabilityView.split('').slice(slices) as CalendarAvailability[],
      prevQuarter,
      this.after24Hours,
      this.timelineSizes()
    );

    const freeEvent = events.find((x) => x.availability === CalendarAvailability.free);

    if (freeEvent) {
      if (freeEvent.start <= this.now) {
        return {
          availableState: 'NOW' as const,
          from: freeEvent.start,
          to: freeEvent.end,
        };
      }
      return {
        availableState: 'SOON' as const,
        from: freeEvent.start,
        to: freeEvent.end,
      };
    } else {
      return {
        availableState: 'UNAVAILABLE' as const,
      };
    }
  });

  resources = injectResources();

  trackEventsByFn(index: number, item: TimelineEvent) {
    return item.id;
  }

  events = rxResource({
    params: () => this.resource(),
    stream: ({ params }) => {
      const now = new Date();
      const nowAddedDay = addHours(now, 24);
      return this.dataService
        .getMyEventsWhereIsResource(now, nowAddedDay, this.resources())
        .pipe(map((e) => e.filter((ev) => ev.resourceAttendees.some((ra) => ra.resource?.id === params.id))));
    },
  });

  multiview = inject(MultiviewComponent);

  confirmEvent(foundEventToConfirm: IdentifiedAssistEvent) {
    // if fast confirm is actrive, confirm directly
    if (this.fastConfirmUntil > new Date()) {
      this.apiService
        .call(
          confirmEvent({
            data: {
              id: foundEventToConfirm.id,
              subject: foundEventToConfirm.subject,
              showAs: foundEventToConfirm.showAs.value,
              realEnd: foundEventToConfirm.end.utcDateTime,
              realStart: foundEventToConfirm.start.utcDateTime,
              resourceDisplayName: this.resource().name,
              resourceUsername: this.resource().email,
              organiserEmail: this.authService.getEmail(),
              organiserName: this.user().data!.givenName + ' ' + this.user().data!.surname,
              iCalUId: foundEventToConfirm.iCalUId,
              action: 'confirm',
              doneBy: 'assist',
            },
          })
        )
        .subscribe((r) => {
          if (r.success) {
            this.notificationService.success(
              this.translocoPipe.transform(translation.qr.detail),
              this.translocoPipe.transform(translation.qr.confirmed),
              this.multiview.vcf
            );
          }

          this.events.reload();
        });
    } else {
      this.qrService.trigger$.next(null);
    }
  }

  cancelParticipation(event: IdentifiedAssistEvent) {
    this.dialog
      .open<number, CancelParticipationData>(ReservationCancelParticipationComponent, {
        data: {
          reservation: event,
        },
      })
      .closed.subscribe((x) => {
        if (x === 1) {
          this.store.dispatch(
            cancelParticipationEventById({
              eventToCancelParticipationId: event.id,
              icalId: event.iCalUId,
            })
          );
        }
      });
  }

  changeEvent(event: IdentifiedAssistEvent) {
    this._dialog = this.reservationDialog.open({
      data: {
        mode: 'detail',
        readonlyOther: false,
        reservation: event,
        triggerChange: true,
      },
      panelClass: 'full-height-modal',
    });
  }

  finishEvent(event: IdentifiedAssistEvent) {
    this.dialog
      .open<number, FinishReservationData>(ReservationFinishComponent, {
        data: {
          reservation: event,
        },
      })
      .closed.subscribe((x) => {
        if (x === 1) {
          this.store.dispatch(
            finishEventById({
              eventToFinishId: event.id,
            })
          );
        }
      });
  }

  deleteEvent(event: IdentifiedAssistEvent) {
    const isOccurrenceLike = event.type === 'occurrence' || event.type === 'exception';
    const isTouchone = this.configService.getMode() === 'touchone-calendar';
    if (isTouchone && isOccurrenceLike && event.occurrenceId) {
      this.deleteEventWithTarget(event, event.seriesMasterId ?? event.id, 'occurrence', {
        scope: 'single',
        occurrenceId: event.occurrenceId,
      });
      return;
    }

    this.deleteEventWithTarget(event, event.id, 'event', { scope: 'all' });
  }

  deleteSeriesEvent(event: IdentifiedAssistEvent) {
    this.deleteEventWithTarget(event, event.seriesMasterId ?? event.id, 'series', { scope: 'all' });
  }

  private deleteEventWithTarget(
    event: IdentifiedAssistEvent,
    targetEventId: string,
    scope: 'event' | 'occurrence' | 'series',
    mutationScope: CalendarRecurringMutationScope
  ) {
    this.dialog
      .open(ReservationDeleteComponent, {
        data: {
          reservation: event,
          scope,
        },
      })
      .closed.subscribe((x) => {
        if (x === 1) {
          this.store.dispatch(
            deleteEventById({
              subject: event.subject,
              showAs: event.showAs.value,
              end: event.end.utcDateTime,
              start: event.start.utcDateTime,
              resourceAttendees: event.resourceAttendees,
              eventToDeleteId: targetEventId,
              icalId: event.iCalUId,
              mutationScope,
            })
          );
        }
      });
  }

  navigateEvent(event: { event: IdentifiedAssistEvent; resourceAttendee: ResourceAttendee }) {
    if (event.resourceAttendee.resource) {
      const id = event.resourceAttendee.resource.idForFiltering;
      this.router.navigate(['/navigation'], {
        queryParams: {
          targetResourceId: id,
        } as NavigationRouteParams,
      });
    }
  }

  detailEvent(event) {
    this._dialog = this.reservationDialog.open({
      data: {
        mode: 'detail',
        readonlyOther: false,
        reservation: event,
        triggerChange: false,
      },
      panelClass: 'full-height-modal',
    });
  }

  showOnFloorplan(
    resource: AssistResourceBM,
    dataItem: {
      event: IdentifiedAssistEvent;
      resourceAttendee: ResourceAttendee;
    }
  ) {
    if (resource) {
      const diffInMinutes = differenceInMinutes(dataItem.event.end.utcDateTime, dataItem.event.start.utcDateTime);
      const diffHours = Math.floor(diffInMinutes / 60);
      const diffMinutes = Math.floor(diffInMinutes % 60);
      const pointer = resource;
      if (pointer.mapInfos.length === 0) {
        return;
      }
      const areaId = pointer.mapInfos[0].areaId;
      this.store.dispatch(
        setFloorplanFilterDateAndDuration({
          args: {
            date: dataItem.event.start.normalize().utcDateTime,
            time: dataItem.event.start.normalize().utcDateTime,
            duration: set(dataItem.event.end.normalize().utcDateTime, {
              hours: diffHours,
              minutes: diffMinutes,
            }),
            areaId: areaId,
            requirements: [],
          },
        })
      );

      this.store.dispatch(
        setFloorplanFilterAreaId({
          args: {
            areaId: areaId,
          },
        })
      );
      void this.router.navigate(['/', 'floorplans'], {
        queryParams: {
          resourceId: resource.id,
        },
      });
    }
  }

  translocoService = inject(TranslocoService);
  translocoPipe = inject(TranslocoPipe);
  configService = inject(ConfigService);

  onFavouriteReserve() {
    const event = prepareEventForDetail(
      this.translocoService,
      this.resource(),
      this.translation,
      this.translocoPipe,
      this.configService,
      null,
      this.reservationPosibilities()!.from,
      this.reservationPosibilities()!.to
    ) as any;

    this._dialog = this.reservationDialog.open({
      data: {
        mode: 'new',
        forcedTab: 0,
        readonlyOther: false,
        closeOnSave: true,
        reservation: event,
        // resource: favourite.resource,
        triggerChange: false,
      },
      panelClass: 'full-height-modal',
    });
    this._dialog.closed.subscribe(() => {
      this.events.reload();
    });
  }

  protected readonly translation = translation;
  protected readonly getTextKeyAndClass = getTextKeyAndClass;
}
